import { useState } from 'react';
import { ActionMenu, Button, Input } from '@/shared/components';
import { invoiceKindLabels, type InvoiceResponse } from '@/shared/api';
import { InvoiceDocumentItem } from './InvoiceDocumentItem';
import { InvoiceDraftItem } from './InvoiceDraftItem';
import { currentInvoice, invoiceHistory } from './bookingInvoiceModel';

interface Props {
  invoice: InvoiceResponse;
  cancelling: boolean;
  discarding: boolean;
  onDownload: (id: string, fileName: string) => void;
  /** `done` schließt das Storno-Formular, sobald der Server den Storno angenommen hat. */
  onCancel: (invoiceId: string, reason: string | null, done: () => void) => void;
  onCorrectRecipient: (invoiceId: string) => void;
  onOpenDraft: (draftId: string) => void;
  onDiscardDraft: (draftId: string) => void;
}

/**
 * Eine Rechnung samt ihrer Gutschrift.
 *
 * <p>Storniert wird je Rechnung, nicht je Buchung. Zwei Gründe, beide fachlich: es kann auch
 * nur der Parkplatz storniert werden, und das Geld liegt woanders — bei OTA-Buchungen hat der
 * Kanal die Übernachtung kassiert, den Parkplatz haben wir selbst. Eine gemeinsame Gutschrift
 * müsste man später aufteilen, und eine ausgestellte Gutschrift teilt man nicht mehr.</p>
 */
export function BookingInvoiceGroup({
  invoice,
  cancelling,
  discarding,
  onDownload,
  onCancel,
  onCorrectRecipient,
  onOpenDraft,
  onDiscardDraft,
}: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [reason, setReason] = useState('');
  const history = invoiceHistory(invoice);
  const current = currentInvoice(invoice);
  const corrections = history.filter((document) => document.id !== current.id);
  const isDamageClaim = invoice.kind === 'DamageClaim';
  const canAccessDocuments = invoice.canAccessDocuments !== false;

  if (invoice.invoiceNumber === null) {
    if (!canAccessDocuments) {
      return <InvoiceDocumentItem invoice={invoice} kind="invoice" onDownload={onDownload} />;
    }
    return (
      <InvoiceDraftItem
        invoice={invoice}
        discarding={discarding}
        onOpenDraft={onOpenDraft}
        onDiscardDraft={onDiscardDraft}
      />
    );
  }

  return (
    <div>
      <p className="text-ink-soft mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        {invoiceKindLabels[invoice.kind]}
      </p>
      <InvoiceDocumentItem
        invoice={current}
        kind="invoice"
        onDownload={onDownload}
        onCancel={
          canAccessDocuments && !invoice.isCancelled && !formOpen
            ? () => setFormOpen(true)
            : undefined
        }
      />
      {/* Ein stornierter Beleg wird nicht mehr korrigiert – der Storno ist sein letzter Stand. */}
      {canAccessDocuments && !isDamageClaim && !invoice.isCancelled && (
        <div className="mt-2 flex justify-end">
          <ActionMenu
            trigger={
              <Button size="sm" variant="secondary" className="min-h-11 sm:min-h-9">
                Weitere Aktionen
              </Button>
            }
            items={[
              {
                label: 'Empfänger korrigieren',
                icon: 'Edit',
                onClick: () => onCorrectRecipient(invoice.id),
              },
            ]}
          />
        </div>
      )}
      {canAccessDocuments && invoice.isCancelled && invoice.cancellationReason && (
        <p className="text-ink-soft mt-2 text-xs">
          <span className="font-medium">Storno-Grund:</span> {invoice.cancellationReason}
        </p>
      )}
      {corrections.length > 0 && (
        <div className="border-hairline mt-3 space-y-3 border-t pt-3">
          <p className="text-ink-soft text-xs font-medium">Belegverlauf</p>
          {corrections.map((correction) => (
            <div key={correction.id} className="space-y-1">
              <InvoiceDocumentItem
                invoice={correction}
                kind={
                  correction.status === 'CreditNote' || correction.correctionKind === 'FullReversal'
                    ? 'cancellation'
                    : 'correction'
                }
                onDownload={onDownload}
              />
              <p className="text-ink-faint text-xs">
                Korrigiert ·{' '}
                <a
                  className="text-ink underline underline-offset-2"
                  href={`#invoice-${current.id}`}
                >
                  Aktuelle Rechnung
                </a>
              </p>
            </div>
          ))}
        </div>
      )}
      {canAccessDocuments && formOpen && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            placeholder={
              isDamageClaim ? 'Grund der Aufhebung (optional)' : 'Storno-Grund (optional)'
            }
            aria-label={isDamageClaim ? 'Grund der Aufhebung' : 'Storno-Grund'}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="flex-1"
          />
          <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)}>
            Abbrechen
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() =>
              onCancel(invoice.id, reason || null, () => {
                setFormOpen(false);
                setReason('');
              })
            }
            loading={cancelling}
          >
            {isDamageClaim ? 'Aufhebung erstellen' : 'Stornorechnung erstellen'}
          </Button>
        </div>
      )}
    </div>
  );
}
